/* eslint-disable react/no-unused-prop-types */
import {
    createContext, useMemo, useState, useEffect,
} from 'react';
import * as React from 'react';
import {
    CARD_THEMES, OrientationType, SizeType, TActionButtonsPosition,
} from '../constants';
import { CardTheme, ImageBackground } from '../types/cardElements';
import { CardLabels, TVariantId } from '../types/productData';

interface ProductCardContextInterface {
    orientation: OrientationType;
    size: SizeType;
    imageBackground: ImageBackground;
    floatPriceToRight: boolean;
    hasHoverEffect: boolean;
    cardLabels: CardLabels;
    isHovered: boolean;
    setIsHovered: Function;
    isThumbnailsHovered: boolean;
    setIsThumbnailsHovered: Function;
    activeVariantId: TVariantId;
    isDisabled: boolean;
    setActiveVariantId: Function;
    actionButtonsPosition: TActionButtonsPosition;
    isSelectable: boolean;
    hasToShowSingleItemDataAsList: boolean;
    isLazy: boolean;
    showSizes: boolean;
    showSizesOnHover: boolean;
    theme: CardTheme;
}

export const ProductCardContext = createContext<ProductCardContextInterface>(
    {} as ProductCardContextInterface,
);

type ProductCardContextProviderInterface = React.PropsWithChildren<{
    imageBackground: ImageBackground;
    hasHoverEffect: boolean;
    cardLabels: CardLabels;
    actionButtonsPosition: TActionButtonsPosition;
    orientation: OrientationType;
    size: SizeType;
    isSelectable: boolean;
    isDisabled: boolean;
    activeVariantId: TVariantId;
    floatPriceToRight: boolean;
    hasToShowSingleItemDataAsList: boolean;
    isLazy: boolean;
    showSizes: boolean;
    showSizesOnHover: boolean;
    theme?: CardTheme;
}>;

function ProductCardContextProvider(props: ProductCardContextProviderInterface) {
    const {
        activeVariantId: activeVariantIdProp,
        actionButtonsPosition,
        size,
        orientation,
        children,
        cardLabels,
        hasToShowSingleItemDataAsList,
        isLazy,
        theme = CARD_THEMES.default,
    } = props;
    const [isHovered, setIsHovered] = useState(false);
    const [isThumbnailsHovered, setIsThumbnailsHovered] = useState(false);
    const [activeVariantId, setActiveVariantId] = useState<TVariantId>(activeVariantIdProp);

    useEffect(() => {
        setActiveVariantId(activeVariantIdProp);
    }, [activeVariantIdProp]);

    const contextValue = useMemo<ProductCardContextInterface>(() => ({
        ...props,
        orientation: orientation ?? 'horizontal',
        size: size ?? 'large',
        isHovered,
        activeVariantId,
        actionButtonsPosition: actionButtonsPosition ?? 'description',
        setIsHovered: (newHoverState: boolean) => { setIsHovered(newHoverState); },
        setActiveVariantId: (variantId: TVariantId) => { setActiveVariantId(variantId); },
        cardLabels,
        hasToShowSingleItemDataAsList,
        isThumbnailsHovered,
        setIsThumbnailsHovered,
        isLazy,
        theme,
    }), [
        props, orientation, size, isHovered, activeVariantId, actionButtonsPosition, cardLabels,
        hasToShowSingleItemDataAsList, isThumbnailsHovered, isLazy, theme,
    ]);

    return (
        <ProductCardContext.Provider value={contextValue}>
            {children}
        </ProductCardContext.Provider>
    );
}

export { ProductCardContextProvider };
